export interface StripMetrics {
    scrollLeft: number;
    scrollWidth: number;
    clientWidth: number;
}

export interface CarouselEdges {
    atStart: boolean;
    atEnd: boolean;
}

/**
 * Whether a horizontally scrolling strip is hard against either end, so the
 * arrow that would do nothing can be hidden rather than left sitting there
 * looking live.
 *
 * Read from scroll position, never from an `active` index: `active` goes stale
 * the moment someone scrolls or swipes instead of clicking an arrow, and in
 * imagery-hero it comes from an IntersectionObserver that is wired up once on
 * mount and never rebuilt.
 *
 * 1px of tolerance at both ends — fractional scroll offsets mean `scrollLeft`
 * rarely lands exactly on 0 or exactly on the maximum, and without the slack
 * the arrow stays live at precisely the position where it does nothing.
 *
 * Shared by Global/carousel.vue and Blocks/imagery-hero.vue. It lives here
 * because the two drifted apart once already: carousel.vue got this behaviour
 * in August while imagery-hero.vue did not, and its always-live arrows went on
 * to produce roughly three quarters of every rageclick on the site during the
 * September 2026 launch.
 */
export function carouselEdges({ scrollLeft, scrollWidth, clientWidth }: StripMetrics): CarouselEdges {
    return {
        atStart: scrollLeft <= 1,
        atEnd: scrollLeft >= scrollWidth - clientWidth - 1,
    };
}

/**
 * Which slide a horizontally scrolling strip is currently showing, derived from
 * scroll position.
 *
 * Read from scroll for the same reason carouselEdges is, and the reason matters:
 * imagery-hero only ever wrote `active` from an IntersectionObserver, which was
 * not firing. `active` therefore sat at 0 while the strip scrolled away, and
 * navigateTo(active - 1) computed navigateTo(-1), clamped to 0, found
 * diff === 0 and returned — a "previous" arrow that was correctly visible,
 * because visibility IS scroll-derived, and did nothing when clicked. Measured
 * in production: 349 such clicks from 80 people in 36 hours, every one reporting
 * from_index 0. "Next" escaped it only because scrollBy is relative.
 *
 * Global/carousel.vue reached the same conclusion for the same symptom (Asana
 * O2-6): scroll or swipe, and an observer-only index stays on the first item.
 *
 * Rounds to the nearest slide so a strip caught mid-scroll reports where it is
 * heading rather than where it left. Returns 0 rather than NaN when the strip
 * has no width or no slides — a NaN `active` makes every comparison against it
 * false and breaks navigation far more confusingly than being wrong about which
 * slide is showing.
 */
export function activeSlideFrom({ scrollLeft, clientWidth }: StripMetrics, total: number): number {
    if (total <= 0 || clientWidth <= 0) return 0;
    return Math.min(Math.max(Math.round(scrollLeft / clientWidth), 0), total - 1);
}
